import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/compat';

import PropTypes from 'prop-types';
import relayEnvironmentProvider from '../../global/relayEnvironmnentProvider.es';

//Components
import FolderWrapper from 'dibs-portfolio-folder/src/components/FolderWrapper';

// Helpers
import asyncPortfolioModalLoader from './asyncPortfolioModalLoader';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

class FolderWrapperWrapper extends Component {
    constructor(props) {
        super(props);

        this.closeModal = this.closeModal.bind(this);
        this.showAuthModal = this.showAuthModal.bind(this);
        this.getModalComponent = this.getModalComponent.bind(this);
    }

    getModalComponent() {
        return new Promise((resolve, reject) => {
            const loadModal = () => {
                asyncPortfolioModalLoader.loadPortfolioModal(
                    AddToFolderModal => (AddToFolderModal ? resolve(AddToFolderModal) : reject())
                );
            };
            if (!this.props.userId) {
                return this.showAuthModal().then(({ err }) => {
                    if (err) {
                        return reject(`AuthModal - ${err}`);
                    }
                    return loadModal();
                });
            } else {
                return loadModal();
            }
        });
    }

    showAuthModal() {
        return new Promise(resolve => {
            this.props.onAuthModalShow();
            authModalLoader
                .show({
                    action: authModalLoader.constants.LOGIN,
                    flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                    ga: {
                        label: this.props.authModalTrackingLabel,
                    },
                })
                .then(resolve);
        });
    }

    closeModal() {
        // Our connection cursors are index-based and not id-based, meaning the cursors for each edge may change as
        // mutations occur. When creating a new folder in the modal, an edge is injected at the top, causing all
        // the cursors to shift down the nodes. This confuses the heck out of relay when using the compat version
        // and it `cannot reconcile edges in a connection`, so the store is never updated properly and the
        // wrong data is displayed despite the network response being correct. The following is a workaround, in which
        // we clear the store of all modal portfolio records, so that relay will enter all data correctly to the store and
        // not be confused when IDs/cursors don't align the previous data's items, as there are no previous records to reference.
        const rs = relayEnvironmentProvider
            .getEnvironment()
            .getStoreData()
            .getRecordStore();
        const viewerId = this.props.viewerId || this.props.viewer.id;
        const connectionIds = rs.getConnectionIDsForField(viewerId, 'portfolios');
        connectionIds
            .filter(
                c =>
                    !(rs.getRangeFilterCalls(c) || []).find(filter => {
                        // However, we also prevent portfolio data that doesn't belong to the modal from being cleared, so that
                        // the consuming application doesn't lose portfolio data if it is queried
                        return (
                            filter.name === 'portfolioIds' ||
                            (Array.isArray(filter.value) &&
                                (filter.value.indexOf('HEART') > -1 ||
                                    filter.value.indexOf('CART') > -1))
                        );
                    })
            )
            .forEach(c => rs.removeRecord(c));

        if (this.props.closeModal) {
            this.props.closeModal();
        }
    }

    render() {
        return (
            <FolderWrapper
                additionalIconClass={this.props.additionalIconClass}
                allowSavingMultipleItems={this.props.allowSavingMultipleItems}
                closeModal={this.closeModal}
                currentPortfolioId={this.props.currentPortfolioId}
                disabledButton={this.props.disabledButton}
                environment={this.context.relay.environment}
                getModalComponent={this.getModalComponent}
                iconPosition={this.props.iconPosition}
                iconText={this.props.iconText}
                loadPortfolioItemData={this.props.loadPortfolioItemData}
                modalHeaderText={this.props.modalHeaderText}
                onAddItemError={this.props.onAddItemError}
                onAddItemSuccess={this.props.onAddItemSuccess}
                onAddNoteError={this.props.onAddNoteError}
                onAddNoteSuccess={this.props.onAddNoteSuccess}
                onCreateFolderError={this.props.onCreateFolderError}
                onCreateFolderSuccess={this.props.onCreateFolderSuccess}
                onFolderClick={this.loadModal}
                onFolderRemove={this.props.onFolderRemove}
                onFolderSave={this.props.onFolderSave}
                onNewFolderSave={this.props.onNewFolderSave}
                onRemoveItemError={this.props.onRemoveItemError}
                onRemoveItemSuccess={this.props.onRemoveItemSuccess}
                primaryItemPk={this.props.primaryItemPk}
                shouldFillInFolder={this.props.shouldFillInFolder}
                singlePortfolioItem={this.props.singlePortfolioItem}
                selectedItemIds={this.props.selectedItemIds}
                selectedItemImages={this.props.selectedItemImages}
                selectedItemTitles={this.props.selectedItemTitles}
                trackingHooks={this.props.trackingHooks}
                userId={this.props.userId}
                userIds={this.props.userIds}
                viewer={this.props.viewer}
            />
        );
    }
}

FolderWrapperWrapper.contextTypes = {
    relay: PropTypes.object.isRequired,
};

// ignore default prop functions from coverage stats
/* istanbul ignore next */
FolderWrapperWrapper.defaultProps = {
    additionalIconClass: '',
    allowSavingMultipleItems: false,
    authModalTrackingLabel: 'save item',
    currentPortfolioId: '',
    disabledButton: false,
    iconPosition: 'absolute',
    iconText: '',
    item: null,
    loadPortfolioData: false,
    loadPortfolios: true,
    onAuthModalShow: () => {},
    onAddItemError: () => {},
    onAddItemSuccess: () => {},
    onAddNoteError: () => {},
    onAddNoteSuccess: () => {},
    onCreateFolderError: () => {},
    onCreateFolderSuccess: () => {},
    onFolderClick: () => {},
    onFolderRemove: () => {},
    onFolderSave: () => {},
    onNewFolderSave: () => {},
    onRemoveItemError: () => {},
    onRemoveItemSuccess: () => {},
    portfolioItemId: '',
    primaryItemPk: '',
    selectedItemIds: [],
    selectedItemImages: [],
    selectedItemTitles: [],
    trackingHooks: {},
    userId: null,
    viewer: {},
};

FolderWrapperWrapper.propTypes = {
    additionalIconClass: PropTypes.string,
    allowSavingMultipleItems: PropTypes.bool,
    authModalTrackingLabel: PropTypes.string,
    closeModal: PropTypes.func,
    currentPortfolioId: PropTypes.string,
    disabledButton: PropTypes.bool,
    iconPosition: PropTypes.string,
    iconText: PropTypes.string,
    item: PropTypes.object,
    loadPortfolioData: PropTypes.bool,
    loadPortfolioItemData: PropTypes.bool,
    loadPortfolios: PropTypes.bool,
    modalHeaderText: PropTypes.string,
    onAuthModalShow: PropTypes.func,
    onAddItemError: PropTypes.func,
    onAddItemSuccess: PropTypes.func,
    onAddNoteError: PropTypes.func,
    onAddNoteSuccess: PropTypes.func,
    onCreateFolderError: PropTypes.func,
    onCreateFolderSuccess: PropTypes.func,
    onFolderClick: PropTypes.func,
    onFolderRemove: PropTypes.func,
    onFolderSave: PropTypes.func,
    onNewFolderSave: PropTypes.func,
    onRemoveItemError: PropTypes.func,
    onRemoveItemSuccess: PropTypes.func,
    portfolioItemId: PropTypes.string,
    primaryItemPk: PropTypes.string,
    selectedItemIds: PropTypes.array,
    selectedItemImages: PropTypes.array,
    selectedItemTitles: PropTypes.array,
    shouldFillInFolder: PropTypes.bool,
    singlePortfolioItem: PropTypes.bool,
    trackingHooks: PropTypes.object,
    viewer: PropTypes.object,
    viewerId: PropTypes.string,
    userId: PropTypes.string,
    userIds: PropTypes.array,
};

export default createFragmentContainer(FolderWrapperWrapper, {
    viewer: graphql`
        fragment FolderWrapperWrapper_viewer on Viewer {
            id
            ...FolderWrapper_viewer
        }
    `,
});

export const folderWrapperQuery = graphql`
    query FolderWrapperWrapperQuery(
        $userIds: [String]!
        $selectedItemIds: [String]!
        $shouldFillInFolder: Boolean!
    ) {
        viewer {
            ...FolderWrapperWrapper_viewer
        }
    }
`;



// WEBPACK FOOTER //
// ./assets/js/global/components/FolderWrapperWrapper.jsx